// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Props:
 *  * navigationOutSide: To add arrows outside swiper container, to fix overflow issue.
 *  * xController: To create custom controller.
 *  * extendedStyles: to append style to the component
 */

/* eslint-disable spire/fenced-imports */
import SwiperCore, { A11y, Autoplay, Controller, Navigation, Pagination, Scrollbar } from "swiper";
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react";
/* eslint-enable spire/fenced-imports */

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import ArrowNavigation, { ArrowNavigationProps } from "@west/App/Components/XngCarousel/ArrowNavigation";
import HorizontalScrollIndicator from "@west/App/Components/XngCarousel/HorizontalScrollIndicator";
import { CustomAppState } from "@west/App/Store/Reducers";
import { xClassNames } from "@west/XDL/Utilities";
import { mergeWithCss } from "@west/XDL/Utilities/mergeWithCss";
import pick from "lodash/pick";
import React, { FC, isValidElement, useEffect, useState } from "react";
import { Transition } from "react-transition-group";
import { Swiper as SwiperClass } from "swiper/types";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Controller, Autoplay]);

export interface SwiperInstance extends SwiperClass {}

export type ArrowPosition = "left" | "right" | "top" | "bottom";

export type ArrowType = "next" | "prev";

export type Props = SwiperProps & {
    shadowNavigation?: boolean;
    arrowStyle?: ArrowNavigationProps["arrowStyle"];
    extendedStyles?: SwiperStyle;
};

export interface SwiperStyle {
    container?: InjectableCss;
}

export const swiperStyle: SwiperStyle = {};

const XngCarousel: FC<Props> = props => {
    const { children, className, extendedStyles, shadowNavigation, arrowStyle, ...swiperProps } = props;
    const [isSwiperCssImported, setIsSwiperCssImported] = useState(false);
    const [showScrollLeft, setShowScrollLeft] = useState(false);
    const [showScrollRight, setShowScrollRight] = useState(false);
    const [styles] = useState(() => mergeWithCss(swiperStyle, extendedStyles));
    const isSmallMobileScreen = useAppSelector(state => (state as CustomAppState).xCommon.isSmallMobileScreen);

    useEffect(() => {
        const getStyle = async () => {
            /* eslint-disable global-require */
            await require("swiper/swiper-bundle.css");
            /* eslint-enable global-require */
        };
        getStyle().then(_ => setIsSwiperCssImported(true));
    }, []);

    if (!isSwiperCssImported) {
        return null;
    }

    if (props.scrollbar === true) {
        swiperProps.scrollbar = { draggable: true, el: ".swiper-scrollbar", dragSize: 50 };
    }

    if (props.pagination === true) {
        swiperProps.pagination = { clickable: true, el: ".swiper-pagination" };
    }

    const showPaginationContainer = !!(
        swiperProps.pagination ||
        swiperProps.scrollbar ||
        (swiperProps.navigation && arrowStyle === "mobile")
    );

    return (
        <StyledWrapper
            {...styles.container}
            className={xClassNames.append(["xng-carousel", className])}
            data-test-selector="xng-carousel"
        >
            <Swiper {...swiperProps} observeParents observer={swiperProps.loop ? undefined : true}>
                {shadowNavigation && (
                    <Transition mountOnEnter unmountOnExit in={showScrollLeft} timeout={{ enter: 75, exit: 250 }}>
                        {state => <HorizontalScrollIndicator direction="left" transitionState={state} />}
                    </Transition>
                )}

                {React.Children.map(children, child => {
                    // for null case
                    if (!isValidElement(child)) {
                        return;
                    }

                    const { props } = child;
                    const swiperSlidePropsKeys = ["tag", "zoom", "virtualIndex"];
                    const childrenProps = omitMultiple(props, [...swiperSlidePropsKeys]);
                    const swiperSlideProps = pick(props, swiperSlidePropsKeys);

                    return (
                        <SwiperSlide {...swiperSlideProps}>
                            {React.createElement(child.type, childrenProps)}
                        </SwiperSlide>
                    );
                })}

                {shadowNavigation && (
                    <Transition mountOnEnter unmountOnExit in={showScrollRight} timeout={{ enter: 75, exit: 250 }}>
                        {state => <HorizontalScrollIndicator direction="right" transitionState={state} />}
                    </Transition>
                )}

                {swiperProps.navigation && (!isSmallMobileScreen || arrowStyle === "default") && (
                    <>
                        <ArrowNavigation
                            arrowStyle="default"
                            arrowType="prev"
                            showPaginationContainer={showPaginationContainer}
                        />
                        <ArrowNavigation
                            arrowStyle="default"
                            arrowType="next"
                            showPaginationContainer={showPaginationContainer}
                        />
                    </>
                )}
                {showPaginationContainer && (
                    <StyledWrapper className="xng-pagination-container">
                        {swiperProps.navigation && arrowStyle === "mobile" && (
                            <ArrowNavigation
                                arrowStyle="mobile"
                                arrowType="prev"
                                showPaginationContainer={showPaginationContainer}
                            />
                        )}
                        {swiperProps.pagination && <div className="swiper-pagination" />}
                        {swiperProps.scrollbar && <div className="swiper-scrollbar" />}
                        {swiperProps.navigation && arrowStyle === "mobile" && (
                            <ArrowNavigation
                                arrowStyle="mobile"
                                arrowType="next"
                                showPaginationContainer={showPaginationContainer}
                            />
                        )}
                    </StyledWrapper>
                )}
            </Swiper>
        </StyledWrapper>
    );
};

XngCarousel.defaultProps = {
    slidesPerView: "auto",
    navigation: true,
    simulateTouch: false,
    spaceBetween: 20,
    pagination: false,
    scrollbar: false,
    watchOverflow: true,
    updateOnWindowResize: true,
    centerInsufficientSlides: false,
    draggable: false,
    freeMode: false,
    shadowNavigation: false,
    arrowStyle: "default",
};

export default XngCarousel;
